import { ShieldX } from "lucide-react";
import { Button } from "@sphinx/design-system/button";
import { Input } from "@sphinx/design-system/input";
import { Label } from "@sphinx/design-system/label";
import { Textarea } from "@sphinx/design-system/textarea";
import { Badge } from "@sphinx/design-system/badge";
import { Separator } from "@sphinx/design-system/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sphinx/design-system/select";
import { PassCriteriaSelector } from "../pass-criteria-selector";
import { cn } from "@/lib/utils";
import { effectiveModels } from "@/lib/client-models";
import type { HostListItem } from "@/hooks/useClients";
import type { TestTemplate } from "@/components/evals/eval-runner/types";
import type { WizardStepKey } from "../constants";
import { YARN_ORGS, type YarnOrg } from "@/shared/sphinx";

interface ReviewStepProps {
  suiteName: string;
  suiteDescription: string;
  minimumPassRate: number;
  selectedServers: string[];
  selectedTargets: HostListItem[];
  validTestTemplates: TestTemplate[];
  /** Yarn environment the run's MCP servers point at. */
  /** Yarn organization whose connector (`yarn-<env>-<org>`) the run attaches. */
  yarnOrg: YarnOrg;
  onYarnOrgChange: (value: YarnOrg) => void;
  /** Running an existing suite: name is read-only, no description / servers. */
  existingSuite?: boolean;
  onSuiteNameChange: (value: string) => void;
  onSuiteDescriptionChange: (value: string) => void;
  onMinimumPassRateChange: (value: number) => void;
  onEditStep: (step: WizardStepKey) => void;
  showNameError?: boolean;
}

export function ReviewStep({
  suiteName,
  suiteDescription,
  minimumPassRate,
  selectedServers,
  selectedTargets,
  validTestTemplates,
  yarnOrg,
  onYarnOrgChange,
  existingSuite = false,
  onSuiteNameChange,
  onSuiteDescriptionChange,
  onMinimumPassRateChange,
  onEditStep,
  showNameError = false,
}: ReviewStepProps) {
  // What the run actually expands to. The server fans out case × client ×
  // model (`createRunInTx`), so a client with two models contributes two
  // iterations per case — say so here rather than letting the run detail be
  // the first place anyone finds out.
  const clientModelTotal = selectedTargets.reduce(
    (total, target) => total + Math.max(1, effectiveModels(target).length),
    0,
  );
  const iterationTotal = clientModelTotal * validTestTemplates.length;

  // Test-case runs never target prod: no environment picker, dev only
  // (David, 2026-08-25). A case may pin its own org(s) on the Tests step;
  // this org is the default for cases that pin nothing.
  const yarnEnvField = (
    <div className="space-y-2">
      <div className="space-y-2">
        <Label htmlFor="yarn-org" className="text-sm font-medium">
          Default org
        </Label>
        <Select
          value={yarnOrg}
          onValueChange={(value) => onYarnOrgChange(value as YarnOrg)}
        >
          <SelectTrigger
            id="yarn-org"
            className="w-40"
            data-testid="eval-yarn-org"
            aria-label="Yarn org"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YARN_ORGS.map((org) => (
              <SelectItem key={org} value={org}>
                {org}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground">
        Runs on the dev Yarn deployment. Cases without pinned orgs use this
        org's connector: <code>yarn-dev-{yarnOrg}</code>.
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      {existingSuite ? (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Suite</p>
            <p className="text-lg font-semibold">{suiteName}</p>
          </div>
          {yarnEnvField}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="suite-name" className="text-sm font-medium">
                Test Suite Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="suite-name"
                type="text"
                value={suiteName}
                onChange={(event) => onSuiteNameChange(event.target.value)}
                placeholder="e.g., Weather API Integration Tests"
                className={cn(
                  "text-lg font-semibold",
                  showNameError &&
                    !suiteName.trim() &&
                    "border-destructive focus-visible:ring-destructive",
                )}
                autoFocus
              />
              {showNameError && !suiteName.trim() && (
                <p className="text-sm text-destructive font-medium">
                  Please provide a name for this test suite
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="suite-description" className="text-sm font-medium">
                Description (optional)
              </Label>
              <Textarea
                id="suite-description"
                value={suiteDescription}
                onChange={(event) => onSuiteDescriptionChange(event.target.value)}
                rows={3}
                placeholder="What does this suite cover?"
                className="resize-none"
              />
            </div>
            {yarnEnvField}
          </div>

          <PassCriteriaSelector
            minimumPassRate={minimumPassRate}
            onMinimumPassRateChange={onMinimumPassRateChange}
          />
        </>
      )}

      <div className="space-y-2">
        <h3 className="text-lg">Review your configuration</h3>
        <p className="text-sm text-muted-foreground">
          After confirming the run, you will see your run begin in the eval
          results tab.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border bg-background p-4">
        {!existingSuite ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Connectors</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedServers.map((server) => (
                    <Badge key={server} variant="outline">
                      {server}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onEditStep("servers")}
              >
                Edit
              </Button>
            </div>
            <Separator />
          </>
        ) : null}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Clients</p>
            {selectedTargets.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedTargets.map((target) => {
                  // Every model this client runs, not just the legacy single
                  // one: two models here IS two iterations per case.
                  const models = effectiveModels(target);
                  return (
                    <Badge key={target.hostId} variant="outline">
                      {target.name}
                      {models.length > 0 ? (
                        <span className="ml-1 text-muted-foreground">
                          · {models.join(", ")}
                        </span>
                      ) : (
                        <span className="ml-1 text-muted-foreground">
                          · provider default
                        </span>
                      )}
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                No clients selected
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onEditStep("targets")}
          >
            Edit
          </Button>
        </div>
        <Separator />
        <div data-testid="eval-review-matrix">
          <p className="text-sm font-medium text-muted-foreground">Matrix</p>
          <p className="mt-2 text-sm">
            {validTestTemplates.length} case
            {validTestTemplates.length === 1 ? "" : "s"} ×{" "}
            {selectedTargets.length} client
            {selectedTargets.length === 1 ? "" : "s"} ×{" "}
            {clientModelTotal === selectedTargets.length
              ? "1 model each"
              : `their models (${clientModelTotal} in total)`}{" "}
            ={" "}
            <span className="font-semibold text-foreground">
              {iterationTotal} iteration{iterationTotal === 1 ? "" : "s"}
            </span>
            {(() => {
              // Repeats (`template.runs`) multiply the same expansion again.
              const runTotal = validTestTemplates.reduce(
                (sum, template) =>
                  sum + Math.max(1, template.runs) * clientModelTotal,
                0,
              );
              return runTotal !== iterationTotal
                ? ` (${runTotal} runs including repeats)`
                : "";
            })()}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            A client with several models enabled runs each of them — one
            iteration per model, recorded against the model that ran it.
          </p>
        </div>
        <Separator />
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">
              {existingSuite ? "Test Cases" : "Test Templates"}
            </p>
            <div className="mt-3 space-y-3">
              {validTestTemplates.map((template, index) => (
                <div
                  key={index}
                  className={cn(
                    "rounded-md border p-3",
                    template.isNegativeTest
                      ? "bg-info/50 border-info/50"
                      : "bg-muted/30",
                  )}
                >
                  <div className="flex items-center gap-2">
                    {template.isNegativeTest && (
                      <Badge
                        variant="outline"
                        className="bg-info/50 text-foreground border-info/50 text-xs"
                      >
                        <ShieldX className="h-3 w-3 mr-1" />
                        Negative
                      </Badge>
                    )}
                    <p className="text-sm font-semibold text-foreground">
                      {template.title}
                    </p>
                  </div>
                  {template.isNegativeTest && template.scenario && (
                    <p className="mt-1 text-xs text-foreground italic">
                      Scenario: {template.scenario}
                    </p>
                  )}
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                    {template.query}
                  </p>
                  {template.expectedOutput && (
                    <p className="mt-1 text-xs text-muted-foreground/80 italic">
                      Expected: {template.expectedOutput}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      {template.runs} run{template.runs === 1 ? "" : "s"}
                    </span>
                    {template.promptTurns &&
                      template.promptTurns.length > 1 && (
                        <span>Turns: {template.promptTurns.length}</span>
                      )}
                    {!template.isNegativeTest &&
                      template.expectedToolCalls.length > 0 && (
                        <span>
                          Tools:{" "}
                          {template.expectedToolCalls
                            .map((tc) => tc.toolName)
                            .join(", ")}
                        </span>
                      )}
                    {template.isNegativeTest && (
                      <span className="text-foreground">
                        Expected: No tools triggered
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {!existingSuite ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onEditStep("tests")}
            >
              Edit
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
